import { DatePicker } from '@mui/lab';
import {
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  // Checkbox,
  // FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback } from 'react';
import * as yup from 'yup';
import FormForeign from '../form/foreign';
import { ForeignStateVM } from 'system/components/shared/foreign-state-list/index';
import FormItalian from '../form/italian';
import PmiTextField from 'shell/components/shared/text-field';

export interface MunicipalityEndedForm {
  code?: string;
  name?: string;
  province?: string;
  foreignState?: string;
  endDate?: DateTime | null;
  capital?: boolean;
  region?: string;
  court?: string;
  cap?: string;
  fiscalCode?: string;
  institutionDate?: DateTime | null;
  flag?: boolean;
  oldCode?: string;
  istatCode?: string;
  territory?: string;
  site?: string;
  isForeign?: boolean;
}

interface FormMunicipalityEndedProps {
  onOk: (form: MunicipalityEndedForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: MunicipalityEndedForm;
  isEdit?: boolean;
  isChangeName?: boolean;
  validationSchema?: yup.AnyObjectSchema;
  isForeign?: boolean;
}

const FormMunicipalityEnded = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit = false,
  isChangeName = false,
  validationSchema,
}: FormMunicipalityEndedProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formMunicipalityEnded = useFormik<MunicipalityEndedForm>({
    initialValues: initialValues || {
      code: '',
      name: '',
      province: '',
      foreignState: '',
      endDate: null,
      capital: false,
      region: '',
      court: '',
      cap: '',
      fiscalCode: '',
      institutionDate: null,
      flag: false,
      oldCode: '',
      istatCode: '',
      territory: '',
      site: '',
      isForeign: false,
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formMunicipalityEnded.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice ISTAT"
                  label="Codice ISTAT *"
                  id="istatCode"
                  name="istatCode"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled || isChangeName || formMunicipalityEnded.values.isForeign}
                  value={
                    formMunicipalityEnded.values.istatCode ||
                    formMunicipalityEnded.values.code ||
                    ''
                  }
                  inputProps={{
                    maxLength: '6',
                  }}
                  onChange={(event) =>
                    formMunicipalityEnded.setFieldValue(
                      'istatCode',
                      event.target.validity.valid
                        ? event.target.value
                        : formMunicipalityEnded.values.istatCode
                    )
                  }
                  error={
                    formMunicipalityEnded.touched.istatCode &&
                    Boolean(formMunicipalityEnded.errors.istatCode)
                  }
                  helperText={formMunicipalityEnded.errors.istatCode}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice comune"
                  label="Codice comune"
                  id="code"
                  name="code"
                  fullWidth
                  inputProps={{
                    maxLength: '6',
                  }}
                  type="text"
                  variant="outlined"
                  disabled
                  value={formMunicipalityEnded.values.code || ''}
                  onChange={(event) =>
                    formMunicipalityEnded.setFieldValue(
                      'code',
                      event.target.validity.valid
                        ? event.target.value
                        : formMunicipalityEnded.values.code
                    )
                  }
                  error={
                    formMunicipalityEnded.touched.code && Boolean(formMunicipalityEnded.errors.code)
                  }
                  helperText={formMunicipalityEnded.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Nome comune"
                  label="Nome comune"
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  variant="outlined"
                  disabled={disabled}
                  value={formMunicipalityEnded.values.name || ''}
                  onChange={(event) => {
                    formMunicipalityEnded.setFieldValue('name', event.target.value);
                  }}
                  error={
                    formMunicipalityEnded.touched.name && Boolean(formMunicipalityEnded.errors.name)
                  }
                  helperText={formMunicipalityEnded.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Comune</FormLabel>
                  <RadioGroup
                    row
                    aria-label="Comune"
                    id="isForeign"
                    value={formMunicipalityEnded.values.isForeign || 'false'}
                    onChange={() =>
                      formMunicipalityEnded.setFieldValue(
                        'isForeign',
                        !formMunicipalityEnded.values.isForeign
                      )
                    }
                  >
                    <FormControlLabel
                      value={false}
                      disabled={disabled || isEdit || isChangeName}
                      control={<Radio />}
                      label="Italiano"
                    />
                    <FormControlLabel
                      value={true}
                      disabled={disabled || isEdit || isChangeName}
                      control={<Radio />}
                      label="Straniero"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {formMunicipalityEnded.values.isForeign ? (
                <FormForeign
                  disabled={disabled || isEdit || isChangeName}
                  isChangeName={isEdit}
                  foreignState={{
                    error: formMunicipalityEnded.errors.foreignState || '',
                    touched: formMunicipalityEnded.touched.foreignState || false,
                    value: formMunicipalityEnded.values.foreignState || '',
                  }}
                  site={{
                    error: formMunicipalityEnded.errors.site || '',
                    touched: formMunicipalityEnded.touched.site || false,
                    value: formMunicipalityEnded.values.site || '',
                  }}
                  territory={{
                    error: formMunicipalityEnded.errors.territory || '',
                    touched: formMunicipalityEnded.touched.territory || false,
                    value: formMunicipalityEnded.values.territory || '',
                  }}
                  fiscalCode={{
                    error: formMunicipalityEnded.errors.fiscalCode || '',
                    touched: formMunicipalityEnded.touched.fiscalCode || false,
                    value: formMunicipalityEnded.values.fiscalCode || '',
                  }}
                  institutionDate={{
                    error: formMunicipalityEnded.errors.institutionDate || '',
                    touched: formMunicipalityEnded.touched.institutionDate || false,
                    value: formMunicipalityEnded.values.institutionDate || null,
                  }}
                  handleChange={formMunicipalityEnded.setFieldValue}
                  onChangeForeignState={(foreignState?: ForeignStateVM) =>
                    formMunicipalityEnded.setValues({
                      ...formMunicipalityEnded.values,
                      istatCode: `${foreignState?.istat}000`,
                      territory: foreignState?.territory,
                      foreignState: foreignState?.id || '',
                      fiscalCode: foreignState?.fiscalCode,
                    })
                  }
                />
              ) : (
                <FormItalian
                  disabled={disabled || isEdit || isChangeName}
                  isChangeName={isEdit}
                  province={{
                    error: formMunicipalityEnded.errors.province || '',
                    touched: formMunicipalityEnded.touched.province || false,
                    value: formMunicipalityEnded.values.province || '',
                  }}
                  capital={{
                    error: formMunicipalityEnded.errors.capital || '',
                    touched: formMunicipalityEnded.touched.capital || false,
                    value: formMunicipalityEnded.values.capital || false,
                  }}
                  cap={{
                    error: formMunicipalityEnded.errors.cap || '',
                    touched: formMunicipalityEnded.touched.cap || false,
                    value: formMunicipalityEnded.values.cap || '',
                  }}
                  court={{
                    error: formMunicipalityEnded.errors.court || '',
                    touched: formMunicipalityEnded.touched.court || false,
                    value: formMunicipalityEnded.values.court || '',
                  }}
                  region={{
                    error: formMunicipalityEnded.errors.region || '',
                    touched: formMunicipalityEnded.touched.region || false,
                    value: formMunicipalityEnded.values.region || '',
                  }}
                  fiscalCode={{
                    error: formMunicipalityEnded.errors.fiscalCode || '',
                    touched: formMunicipalityEnded.touched.fiscalCode || false,
                    value: formMunicipalityEnded.values.fiscalCode || '',
                  }}
                  institutionDate={{
                    error: formMunicipalityEnded.errors.institutionDate || '',
                    touched: formMunicipalityEnded.touched.institutionDate || false,
                    value: formMunicipalityEnded.values.institutionDate || null,
                  }}
                  handleChange={formMunicipalityEnded.setFieldValue}
                />
              )}
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Data Cessazione *"
                  value={formMunicipalityEnded.values.endDate || null}
                  disabled={disabled || isChangeName}
                  onChange={(value) => formMunicipalityEnded.setFieldValue('endDate', value)}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="endDate"
                      name="endDate"
                      fullWidth
                      error={
                        formMunicipalityEnded.touched.endDate &&
                        Boolean(formMunicipalityEnded.errors.endDate)
                      }
                      helperText={formMunicipalityEnded.errors.endDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice comune attuale"
                  label="Codice comune attuale *"
                  id="oldCode"
                  name="oldCode"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '6',
                  }}
                  disabled={disabled}
                  // disabled={disabled || isChangeName || isEdit}
                  value={formMunicipalityEnded.values.oldCode || ''}
                  onChange={(event) =>
                    formMunicipalityEnded.setFieldValue(
                      'oldCode',
                      event.target.validity.valid
                        ? event.target.value
                        : formMunicipalityEnded.values.oldCode
                    )
                  }
                  error={
                    formMunicipalityEnded.touched.oldCode &&
                    Boolean(formMunicipalityEnded.errors.oldCode)
                  }
                  helperText={formMunicipalityEnded.errors.oldCode}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormMunicipalityEnded.displayName = 'Form MunicipalityEnded component';

export default FormMunicipalityEnded;
